import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Box, IconButton, Collapse } from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import API_CONFIG from '../config/api';

const CostBreakerBanner = ({ selectedProject }) => {
  const [breakerStatus, setBreakerStatus] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedProject?.project_id) {
      setLoading(false);
      return;
    }

    const fetchBreakerStatus = async () => {
      try {
        const response = await API_CONFIG.request('/jira/cost/breaker-status', {
          method: 'GET'
        });
        
        if (response.ok) {
          const data = await response.json();
          setBreakerStatus(data);
          // Only show banner if breaker is active or near threshold
          setOpen(data.breaker_active || data.daily_cost >= data.warning_threshold * 0.8);
        } else {
          setBreakerStatus(null);
        }
      } catch (error) {
        console.error('Error fetching breaker status:', error);
        setBreakerStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBreakerStatus();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchBreakerStatus, 30000);
    
    return () => clearInterval(interval);
  }, [selectedProject]);

  if (loading || !breakerStatus) {
    return null;
  }

  const { daily_cost, warning_threshold, hard_stop_threshold, breaker_active } = breakerStatus;
  const percentage = (daily_cost / hard_stop_threshold) * 100;
  const isWarning = daily_cost >= warning_threshold && !breaker_active;
  const isCritical = breaker_active;

  if (!isWarning && !isCritical) {
    return null;
  }

  return (
    <Collapse in={open}>
      <Box sx={{ position: 'sticky', top: 64, zIndex: 1100, width: '100%' }}>
        <Alert
          severity={isCritical ? 'error' : 'warning'}
          icon={<WarningIcon />}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setOpen(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            borderRadius: 0,
            boxShadow: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
            {isCritical ? 'Budget Limit Reached' : 'Budget Warning'}
          </AlertTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
              Daily LLM cost: <strong>${daily_cost.toFixed(2)}</strong> / ${hard_stop_threshold}
              {' '}({percentage.toFixed(1)}%)
            </Box>
            {isCritical ? (
              <Box sx={{ mt: 1 }}>
                <strong>Expensive operations (Figma, RCA, Test Generation) are disabled.</strong>
                {' '}Low-cost operations (Chat, PKB Search) remain available.
                {' '}Budget resets at midnight IST.
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                Approaching budget limit. Expensive operations will be disabled at ${hard_stop_threshold}.
              </Box>
            )}
            {breakerStatus.breakdown && breakerStatus.breakdown.length > 0 && (
              <Box sx={{ mt: 1, fontSize: '0.875rem', opacity: 0.9 }}>
                Breakdown: {breakerStatus.breakdown.map(b => `${b.operation}: $${b.cost.toFixed(2)}`).join(', ')}
              </Box>
            )}
          </Box>
        </Alert>
      </Box>
    </Collapse>
  );
};

export default CostBreakerBanner;

